import { useState, useEffect, useCallback } from 'react';
import '../styles/AdminCierreCaja.css';

function hoy() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

function isoADisplay(iso) {
  // 'yyyy-mm-dd' → 'dd/mm/yyyy'
  const [y,m,d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function displayAIso(disp) {
  // 'dd/mm/yyyy' → 'yyyy-mm-dd'
  const [d,m,y] = disp.split('/');
  return `${y}-${m}-${d}`;
}

const METODO_LABELS = {
  efectivo:     'Efectivo',
  transferencia:'Transferencia',
  debito:       'Débito',
  credito:      'Crédito',
  online:       'Online',
};

export default function AdminCierreCaja({ isOpen, onClose }) {
  const [tab, setTab]           = useState('caja');   // 'caja' | 'historial'
  const [fechaIso, setFechaIso] = useState(displayAIso(hoy()));
  const [resumen, setResumen]   = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError]       = useState('');

  const [cerrando, setCerrando]   = useState(false);
  const [notas, setNotas]         = useState('');
  const [cerradoOk, setCerradoOk] = useState(false);
  const [errCierre, setErrCierre] = useState('');

  const [historial, setHistorial]         = useState([]);
  const [cargandoHist, setCargandoHist]   = useState(false);
  const [detalleHist, setDetalleHist]     = useState(null);

  const cargarResumen = useCallback((fechaISO) => {
    setCargando(true);
    setError('');
    setResumen(null);
    setCerradoOk(false);
    setErrCierre('');
    const display = isoADisplay(fechaISO);
    fetch(`/api/caja/hoy?fecha=${encodeURIComponent(display)}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setResumen(data);
      })
      .catch(err => setError(err.message))
      .finally(() => setCargando(false));
  }, []);

  const cargarHistorial = useCallback(() => {
    setCargandoHist(true);
    fetch('/api/caja/cierres')
      .then(r => r.json())
      .then(data => setHistorial(Array.isArray(data) ? data : []))
      .catch(() => setHistorial([]))
      .finally(() => setCargandoHist(false));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    cargarResumen(fechaIso);
  }, [isOpen, cargarResumen, fechaIso]);

  useEffect(() => {
    if (!isOpen || tab !== 'historial') return;
    cargarHistorial();
  }, [isOpen, tab, cargarHistorial]);

  const cerrarCaja = async () => {
    if (!resumen) return;
    setCerrando(true);
    setErrCierre('');
    try {
      const res = await fetch('/api/caja/cierre', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fecha:              resumen.fecha,
          total_dia:          resumen.total_dia,
          cant_transacciones: resumen.cant_transacciones,
          por_metodo:         resumen.por_metodo,
          por_canal:          resumen.por_canal,
          top_productos:      resumen.top_productos,
          notas,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al registrar cierre');
      setCerradoOk(true);
    } catch (err) {
      setErrCierre(err.message);
    } finally {
      setCerrando(false);
    }
  };

  if (!isOpen) return null;

  const totalMetodos = Object.values(resumen?.por_metodo || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="cc-overlay" onClick={onClose}>
      <div className="cc-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="cc-header">
          <div>
            <h2 className="cc-titulo">Cierre de Caja</h2>
            <p className="cc-subtitulo">Resumen diario de ventas</p>
          </div>
          <button className="cc-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div className="cc-tabs">
          <button className={`cc-tab ${tab === 'caja' ? 'activo' : ''}`} onClick={() => setTab('caja')}>
            📊 Resumen del día
          </button>
          <button className={`cc-tab ${tab === 'historial' ? 'activo' : ''}`} onClick={() => setTab('historial')}>
            🗓 Historial
          </button>
        </div>

        {/* Body */}
        <div className="cc-body">

          {/* ── Caja del día ── */}
          {tab === 'caja' && (
            <>
              {/* Selector de fecha */}
              <div className="cc-fecha-row">
                <label className="cc-fecha-label">Fecha</label>
                <input
                  type="date"
                  className="cc-fecha-input"
                  value={fechaIso}
                  max={displayAIso(hoy())}
                  onChange={e => setFechaIso(e.target.value)}
                />
              </div>

              {cargando && (
                <div className="cc-loading">
                  <span className="cc-spinner" />
                  Cargando resumen…
                </div>
              )}

              {error && <p className="cc-error">⚠ {error}</p>}

              {resumen && !cargando && (
                <>
                  {/* KPIs */}
                  <div className="cc-kpis">
                    <div className="cc-kpi">
                      <span className="cc-kpi-icon">💰</span>
                      <span className="cc-kpi-valor">${resumen.total_dia.toLocaleString('es-AR')}</span>
                      <span className="cc-kpi-label">Total del día</span>
                    </div>
                    <div className="cc-kpi">
                      <span className="cc-kpi-icon">🧾</span>
                      <span className="cc-kpi-valor">{resumen.cant_transacciones}</span>
                      <span className="cc-kpi-label">Transacciones</span>
                    </div>
                    <div className="cc-kpi">
                      <span className="cc-kpi-icon">🏪</span>
                      <span className="cc-kpi-valor">${(resumen.por_canal?.mostrador || 0).toLocaleString('es-AR')}</span>
                      <span className="cc-kpi-label">Mostrador</span>
                    </div>
                    <div className="cc-kpi">
                      <span className="cc-kpi-icon">🌐</span>
                      <span className="cc-kpi-valor">${(resumen.por_canal?.online || 0).toLocaleString('es-AR')}</span>
                      <span className="cc-kpi-label">Online</span>
                    </div>
                  </div>

                  {/* Por método de pago */}
                  {Object.keys(resumen.por_metodo).length > 0 && (
                    <div className="cc-seccion">
                      <h3 className="cc-seccion-titulo">Por método de pago</h3>
                      <div className="cc-metodos">
                        {Object.entries(resumen.por_metodo).map(([metodo, monto]) => (
                          <div key={metodo} className="cc-metodo-row">
                            <span className="cc-metodo-nombre">{METODO_LABELS[metodo] || metodo}</span>
                            <div className="cc-metodo-track">
                              <div
                                className="cc-metodo-fill"
                                style={{ width: totalMetodos > 0 ? `${(monto / totalMetodos) * 100}%` : '0%' }}
                              />
                            </div>
                            <span className="cc-metodo-monto">${monto.toLocaleString('es-AR')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Productos vendidos */}
                  {resumen.top_productos.length > 0 && (
                    <div className="cc-seccion">
                      <h3 className="cc-seccion-titulo">Productos vendidos</h3>
                      <div className="cc-tabla-wrap">
                        <table className="cc-tabla">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Producto</th>
                              <th>Unidades</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resumen.top_productos.map((p, i) => (
                              <tr key={i}>
                                <td className="cc-td-num">{i + 1}</td>
                                <td>{p.nombre}</td>
                                <td className="cc-td-num">{p.unidades}</td>
                                <td className="cc-td-monto">${p.total.toLocaleString('es-AR')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Alertas de stock */}
                  {resumen.alertas_stock.length > 0 && (
                    <div className="cc-seccion">
                      <h3 className="cc-seccion-titulo cc-alerta-titulo">⚠ Stock bajo tras las ventas</h3>
                      <div className="cc-alertas">
                        {resumen.alertas_stock.map((a, i) => (
                          <div key={i} className="cc-alerta-item">
                            <span className="cc-alerta-nombre">{a.nombre}</span>
                            <span className="cc-alerta-stock">
                              Stock: <strong>{a.stock}</strong> / mín: {a.stock_minimo}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sin ventas */}
                  {resumen.cant_transacciones === 0 && (
                    <p className="cc-vacio">No hay ventas registradas para esta fecha.</p>
                  )}

                  {/* Cierre de caja */}
                  {resumen.cant_transacciones > 0 && (
                    <div className="cc-cierre-box">
                      <h3 className="cc-cierre-titulo">Registrar cierre</h3>
                      <textarea
                        className="cc-notas"
                        placeholder="Notas del cierre (opcional)…"
                        rows="2"
                        value={notas}
                        onChange={e => setNotas(e.target.value)}
                        disabled={cerrando || cerradoOk}
                      />
                      {errCierre && <p className="cc-error">⚠ {errCierre}</p>}
                      {cerradoOk ? (
                        <p className="cc-ok">✓ Cierre registrado correctamente.</p>
                      ) : (
                        <button
                          className="cc-btn-cierre"
                          onClick={cerrarCaja}
                          disabled={cerrando}
                        >
                          {cerrando ? 'Registrando…' : '🔒 Registrar cierre del día'}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Historial ── */}
          {tab === 'historial' && (
            <>
              {cargandoHist && (
                <div className="cc-loading"><span className="cc-spinner" />Cargando…</div>
              )}
              {!cargandoHist && historial.length === 0 && (
                <p className="cc-vacio">No hay cierres registrados aún.</p>
              )}
              {detalleHist ? (
                <div className="cc-hist-detalle">
                  <button className="cc-back" onClick={() => setDetalleHist(null)}>← Volver</button>
                  <div className="cc-hist-detalle-header">
                    <span className="cc-hist-fecha-grande">{detalleHist.fecha}</span>
                    <span className="cc-hist-hora">Cierre: {detalleHist.hora_cierre}</span>
                    <span className="cc-hist-total-grande">${detalleHist.total_dia.toLocaleString('es-AR')}</span>
                  </div>
                  <div className="cc-metodos">
                    {Object.entries(detalleHist.por_metodo || {}).map(([m, v]) => (
                      <div key={m} className="cc-metodo-row">
                        <span className="cc-metodo-nombre">{METODO_LABELS[m] || m}</span>
                        <div className="cc-metodo-track">
                          <div className="cc-metodo-fill" style={{ width: detalleHist.total_dia > 0 ? `${(v / detalleHist.total_dia) * 100}%` : '0%' }} />
                        </div>
                        <span className="cc-metodo-monto">${v.toLocaleString('es-AR')}</span>
                      </div>
                    ))}
                  </div>
                  {detalleHist.top_productos?.length > 0 && (
                    <div className="cc-tabla-wrap" style={{ marginTop: 16 }}>
                      <table className="cc-tabla">
                        <thead><tr><th>#</th><th>Producto</th><th>Unidades</th><th>Total</th></tr></thead>
                        <tbody>
                          {detalleHist.top_productos.map((p, i) => (
                            <tr key={i}>
                              <td className="cc-td-num">{i+1}</td>
                              <td>{p.nombre}</td>
                              <td className="cc-td-num">{p.unidades}</td>
                              <td className="cc-td-monto">${p.total.toLocaleString('es-AR')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {detalleHist.notas && (
                    <p className="cc-hist-notas">📝 {detalleHist.notas}</p>
                  )}
                </div>
              ) : (
                <ul className="cc-hist-lista">
                  {historial.map(cierre => (
                    <li key={cierre._id} className="cc-hist-item" onClick={() => setDetalleHist(cierre)}>
                      <div className="cc-hist-item-top">
                        <span className="cc-hist-item-fecha">{cierre.fecha}</span>
                        <span className="cc-hist-item-hora">cierre {cierre.hora_cierre}</span>
                      </div>
                      <div className="cc-hist-item-bottom">
                        <span className="cc-hist-item-total">${cierre.total_dia.toLocaleString('es-AR')}</span>
                        <span className="cc-hist-item-count">{cierre.cant_transacciones} transacciones</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}
